import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FileUploader, FileItem } from 'ng2-file-upload/ng2-file-upload';
import { environment } from '../../../../../environments/environment';
import { FindingService } from '../../../intake/services/finding/finding.service'
import Swal from 'sweetalert2';
import { LockoutComponent } from '../../../lockout/components/lockout/lockout.component';
import { ImageEditorComponent, ImageEditorResult } from '../../../../shared/components/image-editor/image-editor.component';

interface PreviewImage {
  file: File;
  dataUrl: string | ArrayBuffer | null;
  rotation: number;
  queueIndex: number; // index in uploader.queue
}

@Component({
    selector: 'app-finding-image',
    templateUrl: './finding-image.component.html',
    styleUrls: ['./finding-image.component.scss']
})
export class FindingImageComponent implements OnInit {
    private apiURL = environment.api;
    imageValidations = false;
    isUpdated = false;
    showLoader = false;
    uploader: FileUploader;
    previewImages: PreviewImage[] = [];

    existingFindingImages: string[]

    @Input() findingId;
    @Input() lockoutSetting;
    @Input() inspection_report_is_sent;

    @ViewChild('selectedFile') selectedFile: any;

    constructor(public activeModal: NgbActiveModal, private findingService: FindingService, private modelService: NgbModal) {
        let app_token = localStorage.getItem('app_token');
        this.uploader = new FileUploader({
          url: this.apiURL + 'saveFindingImage',
          itemAlias: 'photo',
          authToken: `Bearer ${app_token}`,
          autoUpload: false,
        });
        
    }



    public imagePath = this.findingService.imagePath;

    ngOnInit() {
      this.getFindingImages();
      this.fileUploadOperations();
      
      this.uploader.onBeforeUploadItem = (item) => {
        item.withCredentials = false;
      };

      this.uploader.onAfterAddingFile = (fileItem: any) => {
        fileItem.withCredentials = false; // <-- keep this here too

        const reader = new FileReader();
        const queueIndex = this.uploader.queue.length - 1;

        reader.onload = (event: any) => {
          this.previewImages.push({
            file: fileItem._file,
            dataUrl: event.target.result,
            rotation: 0,
            queueIndex: queueIndex,
          });
        };
        reader.readAsDataURL(fileItem._file);
      };
    }

    rotateLeft(index: number) {
        this.previewImages[index].rotation -= 90;
    }

    rotateRight(index: number) {
        this.previewImages[index].rotation += 90;
    }

    // Opens the shared image editor (line / arrow / square / circle + rotate).
    // Whatever comes back already has the rotation and the drawings baked in,
    // so the preview rotation goes back to 0.
    editImage(index: number) {
      const preview = this.previewImages[index];
      if (!preview) {
        return;
      }

      const modalRef = this.modelService.open(ImageEditorComponent, {
        size: 'lg',
        windowClass: 'image-editor-modal',
        backdrop: 'static',
        keyboard: false
      });
      modalRef.componentInstance.imageSrc = preview.dataUrl;
      modalRef.componentInstance.fileName = preview.file.name;
      modalRef.componentInstance.mimeType = preview.file.type;
      modalRef.componentInstance.initialRotation = preview.rotation;
      modalRef.componentInstance.title = 'Edit Finding Image';

      modalRef.result.then((result: ImageEditorResult) => {
        if (!result) {
          return;
        }
        preview.dataUrl = result.dataUrl;
        preview.file = result.file;
        preview.rotation = 0;

        // keep the upload queue pointing at the edited file
        if (this.uploader.queue[index]) {
          this.uploader.queue[index]._file = result.file;
        }
      }).catch(() => {
        // dismissed, nothing to do
      });
    }

    // Clears one file (by index) or everything if index is omitted
    clearSelectedFile(index?: number): void {
      if (typeof index === 'number') {
        // remove just that one
        if (this.uploader.queue[index]) {
          this.uploader.queue[index].remove(); // removes from queue safely
        }
        if (this.previewImages && this.previewImages[index]) {
          this.previewImages.splice(index, 1);
        }
        // re-sync queueIndex if you use it elsewhere
        if (this.previewImages) {
          this.previewImages.forEach((img, idx) => (img.queueIndex = idx));
        }
      } else {
        // remove everything
        this.uploader.clearQueue();
        if (this.previewImages) this.previewImages = [];
      }

      // optional resets if you had single-image state
      (this as any).selectedImage = null;
      (this as any).rotation = 0;
      (this as any).originalFile = null;
      this.showLoader = false;
      this.imageValidations = false;
    }


    fileUploadOperations() {
      this.uploader.onBuildItemForm = (fileItem: any, form: any) => {
        form.append('findingId', this.findingId);
      };

      this.uploader.onProgressAll = (progress: any) => {
        this.showLoader = true;
      };

      this.uploader.onCompleteItem = (item: any, response: any, status: any) => {
          let result: any = null;
          try { if (response) result = JSON.parse(response); } catch {}
          // If this file failed validation, remove just this file
          if (result && result.status === 400) {
            const idx = this.uploader.queue.indexOf(item);
            this.clearSelectedFile(idx); // remove only the bad one
            this.imageValidations = true;
          }
        };

        this.uploader.onCompleteAll = () => {
          this.showLoader = false;
          this.imageValidations = false;
          // finished uploading all → clear and close
          this.clearSelectedFile(); // clears remaining previews/queue
          this.activeModal.close({ case: 'upload_finding_image' });
        };
    }

    
    uploadFindingImages() {
      const openpopup = this.openFindingLockoutPopup();
      openpopup.then((value) => {
        if (value) {
          // Replace files with rotated versions, then upload
          this.replaceWithRotatedFiles().then(() => {
            this.uploader.uploadAll();
          });
        } else {
          this.previewImages = [];
          this.uploader.clearQueue();
          this.activeModal.close();
        }
      });
    }

    private replaceWithRotatedFiles(): Promise<void> {
      return new Promise((resolve) => {
        let processed = 0;

        this.previewImages.forEach((img, idx) => {
          this.createRotatedFile(img.file, img.rotation, (rotatedFile: File) => {
            this.uploader.queue[idx]._file = rotatedFile; // replace file
            processed++;

            if (processed === this.previewImages.length) {
              resolve(); // only once, after ALL are done
            }
          });
        });
      });
    }

    private createRotatedFile(file: File, rotation: number, callback: (file: File) => void): void {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e: any) => {
        img.src = e.target.result;
      };

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        const angle = (rotation % 360 + 360) % 360;
        let width = img.width;
        let height = img.height;

        if (angle === 90 || angle === 270) {
          canvas.width = height;
          canvas.height = width;
        } else {
          canvas.width = width;
          canvas.height = height;
        }

        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((angle * Math.PI) / 180);
        ctx.drawImage(img, -width / 2, -height / 2);

        canvas.toBlob((blob) => {
          if (blob) {
            const rotatedFile = new File([blob], file.name, { type: file.type });
            callback(rotatedFile);
          }
        }, file.type);
      };

      reader.readAsDataURL(file);
    }

    getFindingImages() {
        this.findingService.getFindingImage(this.findingId).subscribe(
            data => {
                this.existingFindingImages = data.result.data;
            }
        )
    }

    removeImage(finding_image_id) {
        Swal({
            title: 'Are you sure?',
            text: 'Remove Finding Image!',
            type: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, remove it!',
            cancelButtonText: 'No, keep it'
        }).then((result) => {
            if (result.value) {
                this.findingService.deleteFindingImage(finding_image_id).subscribe(
                    data => {
                        this.isUpdated = true;
                        this.getFindingImages();
                        Swal(
                            'Removed!',
                            'Finding Image has been removed.',
                            'success'
                        )
                    }
                );

            } else if (result.dismiss === Swal.DismissReason.cancel) {

            }
        })
    }

    closeClick() {
        if (this.isUpdated) {
            this.activeModal.close({ case: 'remove_finding_image' })
        } else {
            this.activeModal.close('Close click')
        }
    }

    // clearSelectedFile() {
    //     this.uploader.queue[0].remove()
    //     this.selectedFile.nativeElement.value = '';
    // }

    // open popup modal for lockout pin
    openFindingLockoutPopup() {
        return new Promise((resolve) => {
            if (this.inspection_report_is_sent && this.lockoutSetting && this.lockoutSetting.lockout_active !== "") {
                const modalRef = this.modelService.open(LockoutComponent);
                modalRef.componentInstance.lockout_type = "findings";
                modalRef.componentInstance.lockoutSetting = this.lockoutSetting;
                modalRef.result.then((result) => {
                    resolve(result);
                }).catch((error) => {
                    resolve(false);
                });
            }
            else {
                resolve(true);
            }
        });
    }

}
